import * as z from 'zod/mini';
import { RACK_KINDS, RACK_MAX_UNITS, RACK_ROLE_MAX_LENGTH } from '../constants.ts';
import { identifierSchema } from '../naming.ts';
import {
  idSchema,
  nameSchema,
  shortTextSchema,
  sortOrderSchema,
  timestampSchema,
} from './common.ts';

/** A rack's form factor (ROADMAP A12); '' means not set. */
export const rackKindSchema = z.enum(['', ...RACK_KINDS]);
export type RackKind = z.infer<typeof rackKindSchema>;

export const rackSchema = z.object({
  id: idSchema,
  roomId: idSchema,
  name: z.string(),
  /** Short path segment; blank only in a cache made before migration 0051. */
  identifier: z._default(z.string(), ''),
  /** Physical depth (e.g. `24"`), from RACK_DEPTH_CHOICES; empty when not set. */
  depth: z.string(),
  /** How it is mounted (RACK_MOUNT_TYPES); empty when not set. */
  mountType: z.string(),
  /** Form factor (RACK_KINDS); empty when not set. */
  kind: z._default(rackKindSchema, ''),
  /** Role or purpose on site (MDF, IDF, AV…); empty when not set. */
  role: z._default(z.string(), ''),
  /** Height in rack units, or null when never set. */
  units: z.nullable(z.int()),
  sortOrder: sortOrderSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type Rack = z.infer<typeof rackSchema>;

/** `24" · Wall` from a rack's depth and mount; empty when neither is set. */
export function rackDimensions(rack: Pick<Rack, 'depth' | 'mountType'>): string {
  return [rack.depth, rack.mountType].filter((part) => part !== '').join(' · ');
}

// A patch is a partial of the plain fields, so an edit that leaves the
// height out does not reset it.
const rackFields = {
  name: nameSchema,
  identifier: z.optional(identifierSchema),
  depth: shortTextSchema,
  mountType: shortTextSchema,
  kind: rackKindSchema,
  role: z.string().check(z.maxLength(RACK_ROLE_MAX_LENGTH)),
  units: z.nullable(z.int().check(z.gte(1), z.lte(RACK_MAX_UNITS))),
};

export const rackInputSchema = z.object({
  ...rackFields,
  kind: z._default(rackFields.kind, ''),
  role: z._default(rackFields.role, ''),
  units: z._default(rackFields.units, null),
});
export type RackInput = z.infer<typeof rackInputSchema>;

export const rackPatchSchema = z.partial(z.object(rackFields));
export type RackPatch = z.infer<typeof rackPatchSchema>;

/** Body of PUT /api/racks/:id/order — every item id in the rack, once. */
export const rackOrderSchema = z.object({
  itemIds: z.array(idSchema),
});
